/* =================== ONLINE PAYMENT API SERVICES =================== */

import {
  RecurringPlanStatusEnum,
  type RecurringPlanClientType,
  type CustomerRecurringSubscriptionClientType,
} from '@am3nd-org/pointcraft-types';

import { ApiMethods, apiSenderV2 } from 'src/utils/axios';

import type { CreateRecurringPlanDTO } from '../schemas/recurringPlan';
import type {
  Customer,
  Employee,
  Transaction,
  ApiResponse,
  ActivityItem,
  CustomerFilters,
  BusinessProfile,
  TransactionFilters,
} from '../types';

/* =================== API ENDPOINTS =================== */

const API_BASE = '/api/online-payment';

const API_ENDPOINTS = {
  customers: `${API_BASE}/customers`,
  transactions: `${API_BASE}/transactions`,
  activities: `${API_BASE}/activities`,
  employees: `${API_BASE}/employees`,
  plans: `${API_BASE}/plans`,
  businessProfile: `${API_BASE}/business-profile`,
  subscriptions: {
    base: '/api/subscriptions',
    customer: {
      base: '/api/subscriptions/customer',
      byId: (customerId: string) => `/api/subscriptions/customer/${customerId}`,
      cancel: '/api/subscriptions/customer/cancel',
    },
  },
} as const;

/* =================== GENERIC API UTILITIES =================== */

/**
 * HELPER: DELAY FUNCTION FOR RETRY LOGIC
 */
const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * HELPER: CHECK IF ERROR IS RETRYABLE
 *
 * RETRYABLE ERRORS:
 * - NETWORK TIMEOUTS
 * - 503 SERVICE UNAVAILABLE
 * - 502 BAD GATEWAY
 * - 429 TOO MANY REQUESTS
 *
 * NON-RETRYABLE ERRORS:
 * - 4xx CLIENT ERRORS (INVALID DATA, AUTH, ETC.)
 */
function isRetryableError(error: any): boolean {
  if (!error?.response) {
    // NETWORK ERROR (NO RESPONSE) - RETRYABLE
    return true;
  }

  const { status } = error.response;

  // RETRY ON THESE HTTP STATUS CODES
  return status === 429 || status === 502 || status === 503 || status === 504;
}

/**
 * API REQUEST WITH RETRY AND EXPONENTIAL BACKOFF
 *
 * USE THIS FOR CRITICAL OPERATIONS THAT SHOULD RETRY ON TRANSIENT FAILURES
 *
 * FEATURES:
 * - EXPONENTIAL BACKOFF (1s, 2s, 4s, 8s...)
 * - REQUEST CANCELLATION VIA AbortController
 * - ONLY RETRIES ON TRANSIENT ERRORS (NETWORK, 5xx)
 * - CONFIGURABLE MAX RETRIES
 *
 * BEST PRACTICES:
 * - USE FOR: PAYMENT PROCESSING, ORDER CREATION, CRITICAL DATA SYNC
 * - DON'T USE FOR: SIMPLE GET REQUESTS, REAL-TIME SEARCH, CLIENT ERRORS (4xx)
 *
 * EXAMPLE:
 * const result = await apiRequestWithRetry<Transaction>(
 *   '/api/transactions',
 *   ApiMethods.POST,
 *   transactionData,
 *   { retries: 3, abortSignal: controller.signal }
 * );
 */
async function apiRequestWithRetry<TResponse>(
  endpoint: string,
  method: ApiMethods,
  data?: unknown,
  options?: {
    retries?: number;
    abortSignal?: AbortSignal;
    errorCode?: string;
  }
): Promise<ApiResponse<TResponse>> {
  const maxRetries = options?.retries ?? 3;
  const errorCode = options?.errorCode || 'API_ERROR';

  let lastError: any;

  for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
    try {
      // CHECK IF REQUEST WAS CANCELLED
      if (options?.abortSignal?.aborted) {
        throw new Error('Request cancelled');
      }

      // eslint-disable-next-line no-await-in-loop
      const response = await apiSenderV2(endpoint, method, data, true);

      // SUCCESS!
      if (method === ApiMethods.DELETE) {
        return {
          data: response,
          success: true,
        };
      }

      return {
        data: response,
        success: true,
      };
    } catch (error) {
      lastError = error;

      // DON'T RETRY IF:
      // 1. REQUEST WAS CANCELLED
      // 2. THIS WAS THE LAST ATTEMPT
      // 3. ERROR IS NOT RETRYABLE (4xx CLIENT ERRORS)
      if (options?.abortSignal?.aborted || attempt === maxRetries || !isRetryableError(error)) {
        break;
      }

      // EXPONENTIAL BACKOFF: 1s, 2s, 4s, 8s...
      const backoffMs = 2 ** attempt * 1000;

      // LOG RETRY ATTEMPT (ONLY IN DEVELOPMENT)
      if (process.env.NODE_ENV === 'development') {
        console.warn(
          `[API RETRY] Attempt ${attempt + 1}/${maxRetries + 1} failed. ` +
            `Retrying in ${backoffMs}ms...`,
          { endpoint, error }
        );
      }
      // eslint-disable-next-line no-await-in-loop
      await delay(backoffMs);
    }
  }

  // ALL RETRIES FAILED - RETURN ERROR
  const sanitizedDetails =
    process.env.NODE_ENV === 'development'
      ? lastError
      : {
          message: lastError instanceof Error ? lastError.message : 'An error occurred',
        };

  return {
    data: null as unknown as TResponse,
    success: false,
    error: {
      code: errorCode,
      details: sanitizedDetails,
    },
  };
}

/**
 * GENERIC API REQUEST HANDLER WITH CONSISTENT ERROR HANDLING
 *
 * USE THIS FOR STANDARD API CALLS (NO RETRY LOGIC)
 */
async function apiRequest<TResponse>(
  endpoint: string,
  method: ApiMethods,
  data?: unknown,
  errorCode?: string
): Promise<ApiResponse<TResponse>> {
  try {
    const response = await apiSenderV2(endpoint, method, data, false);

    // CHECK FOR ERROR RESPONSE (API returns { errorCode: "..." } on failure)
    if (response?.errorCode) {
      return {
        data: null as unknown as TResponse,
        success: false,
        error: {
          code: response.errorCode,
          details: { message: response.message || 'An error occurred' },
        },
      };
    }

    // EXTRACT DATA FROM RESPONSE (handle both results and result formats)
    const responseData = response?.results ?? response?.result ?? response;

    // HANDLE DELETE RESPONSE
    if (method === ApiMethods.DELETE) {
      return {
        data: responseData ?? (true as unknown as TResponse),
        success: true,
      };
    }
    // HANDLE OTHER RESPONSES
    return {
      data: responseData ?? (null as unknown as TResponse),
      success: true,
    };
  } catch (error) {
    // SANITIZE ERROR DETAILS IN PRODUCTION TO PREVENT INFORMATION LEAKAGE.
    const sanitizedDetails =
      process.env.NODE_ENV === 'development'
        ? error
        : { message: error instanceof Error ? error.message : 'An error occurred' };
    return {
      data: null as unknown as TResponse,
      success: false,
      error: {
        code: errorCode || 'API_ERROR',
        details: sanitizedDetails,
      },
    };
  }
}

/**
 * GENERIC CRUD API FACTORY
 * CREATES A COMPLETE REST API INTERFACE FOR ANY RESOURCE
 */
function createCrudApi<T, CreateDTO = Omit<T, 'id'>, UpdateDTO = Partial<T>, FilterDTO = unknown>(
  baseEndpoint: string,
  resourceName: string
) {
  const errorPrefix = resourceName.toUpperCase();
  return {
    /**
     * GET ALL RESOURCES
     */
    getAll: async (filters?: FilterDTO): Promise<ApiResponse<T[]>> =>
      apiRequest<T[]>(baseEndpoint, ApiMethods.GET, filters, `${errorPrefix}_FETCH_ERROR`).then(
        (res) => ({ ...res, data: res.data || [] })
      ),

    /**
     * GET RESOURCE BY ID
     */
    getById: async (id: string): Promise<ApiResponse<T | null>> =>
      apiRequest<T | null>(
        `${baseEndpoint}/${id}`,
        ApiMethods.GET,
        null,
        `${errorPrefix}_FETCH_ERROR`
      ),

    /**
     * CREATE NEW RESOURCE
     */
    create: async (payload: CreateDTO): Promise<ApiResponse<T>> =>
      apiRequest<T>(baseEndpoint, ApiMethods.POST, payload, `${errorPrefix}_CREATE_ERROR`),

    /**
     * UPDATE EXISTING RESOURCE
     */
    update: async (id: string, payload: UpdateDTO): Promise<ApiResponse<T | null>> =>
      apiRequest<T>(
        `${baseEndpoint}/${id}`,
        ApiMethods.PUT,
        payload,
        `${errorPrefix}_UPDATE_ERROR`
      ),

    /**
     * DELETE RESOURCE
     */
    delete: async (id: string): Promise<ApiResponse<boolean>> =>
      apiRequest<boolean>(
        `${baseEndpoint}/${id}`,
        ApiMethods.DELETE,
        null,
        `${errorPrefix}_DELETE_ERROR`
      ),
  };
}

/* =================== RESOURCE API SERVICES =================== */

/**
 * CUSTOMER API
 * FULL CRUD OPERATIONS FOR CUSTOMER MANAGEMENT
 */
export const customerApi = createCrudApi<
  Customer,
  Omit<Customer, 'id' | 'joinedDate' | 'totalSpent' | 'transactionCount'>,
  Partial<Customer>,
  CustomerFilters
>(API_ENDPOINTS.customers, 'customer');

/**
 * TRANSACTION API
 * FULL CRUD OPERATIONS FOR TRANSACTION MANAGEMENT
 */
export const transactionApi = createCrudApi<
  Transaction,
  Omit<Transaction, 'id' | 'date'>,
  Partial<Transaction>,
  TransactionFilters
>(API_ENDPOINTS.transactions, 'transaction');

/**
 * ACTIVITY API
 * FULL CRUD OPERATIONS FOR ACTIVITY MANAGEMENT
 * NOTE: ACTIVITIES ARE TYPICALLY SYSTEM-GENERATED
 */
export const activityApi = createCrudApi<
  ActivityItem,
  Omit<ActivityItem, 'id' | 'timestamp'>,
  Partial<ActivityItem>
>(API_ENDPOINTS.activities, 'activity');

/**
 * EMPLOYEE API
 * CUSTOM ENDPOINTS FOR EMPLOYEE MANAGEMENT
 * - Create: POST /api/business/users
 * - Update: PUT /api/business/users/:userId
 */
export const employeeApi = {
  /**
   * CREATE NEW EMPLOYEE
   */
  create: async (payload: Omit<Employee, 'id' | 'joinedDate'>): Promise<ApiResponse<Employee>> =>
    apiRequest<Employee>('/api/business/users', ApiMethods.POST, payload, 'EMPLOYEE_CREATE_ERROR'),

  /**
   * UPDATE EXISTING EMPLOYEE
   */
  update: async (id: string, payload: Partial<Employee>): Promise<ApiResponse<Employee | null>> =>
    apiRequest<Employee>(`/api/business/users/${id}`, ApiMethods.PUT, payload, 'EMPLOYEE_UPDATE_ERROR'),

  /**
   * DELETE EMPLOYEE
   */
  delete: async (id: string): Promise<ApiResponse<boolean>> =>
    apiRequest<boolean>(`/api/business/users/${id}`, ApiMethods.DELETE, null, 'EMPLOYEE_DELETE_ERROR'),
};

/**
 * SUBSCRIPTION PLAN API
 * API FOR RECURRING PLAN MANAGEMENT
 *
 * Endpoints:
 * - GET /api/subscriptions - Fetch all plans
 * - POST /api/subscriptions - Create a plan
 * - PUT /api/subscriptions - Update plan status (only id and status)
 */
export const subscriptionPlanApi = {
  /**
   * GET ALL RECURRING PLANS
   */
  getAll: async (): Promise<ApiResponse<RecurringPlanClientType[]>> =>
    apiRequest<RecurringPlanClientType[]>(
      API_ENDPOINTS.subscriptions.base,
      ApiMethods.GET,
      null,
      'SUBSCRIPTION_FETCH_ERROR'
    ).then((res) => ({ ...res, data: res.data || [] })),

  /**
   * CREATE NEW RECURRING PLAN
   */
  create: async (
    plan: CreateRecurringPlanDTO
  ): Promise<ApiResponse<{ success: boolean; planId?: string }>> =>
    apiRequest<{ success: boolean; planId?: string }>(
      API_ENDPOINTS.subscriptions.base,
      ApiMethods.POST,
      plan,
      'SUBSCRIPTION_CREATE_ERROR'
    ),

  /**
   * UPDATE PLAN STATUS
   * Note: Only status can be updated per API spec
   */
  updateStatus: async (
    id: string,
    status: RecurringPlanStatusEnum
  ): Promise<ApiResponse<{ success: boolean; planId?: string }>> =>
    apiRequest<{ success: boolean; planId?: string }>(
      API_ENDPOINTS.subscriptions.base,
      ApiMethods.PUT,
      { id, status },
      'SUBSCRIPTION_UPDATE_ERROR'
    ),
};

/**
 * CUSTOMER SUBSCRIPTION API
 * API FOR MANAGING CUSTOMER SUBSCRIPTIONS
 *
 * Endpoints:
 * - GET /api/subscriptions/customer/{customerId} - Get customer's subscriptions
 * - POST /api/subscriptions/customer - Create customer subscription
 * - POST /api/subscriptions/customer/cancel - Cancel customer subscription
 */
export const customerSubscriptionApi = {
  /**
   * GET CUSTOMER'S SUBSCRIPTIONS
   * Returns the customer's subscriptions
   */
  getByCustomerId: async (
    customerId: string
  ): Promise<ApiResponse<CustomerRecurringSubscriptionClientType | null>> =>
    apiRequest<CustomerRecurringSubscriptionClientType | null>(
      API_ENDPOINTS.subscriptions.customer.byId(customerId),
      ApiMethods.GET,
      null,
      'CUSTOMER_SUBSCRIPTION_FETCH_ERROR'
    ),

  /**
   * CREATE CUSTOMER SUBSCRIPTION
   * Links a customer to a recurring plan with a payment method
   */
  create: async (payload: {
    customerId: string;
    planId: string;
    paymentMethodId: string;
  }): Promise<ApiResponse<{ success: boolean; planId?: string }>> =>
    apiRequest<{ success: boolean; planId?: string }>(
      API_ENDPOINTS.subscriptions.customer.base,
      ApiMethods.POST,
      payload,
      'CUSTOMER_SUBSCRIPTION_CREATE_ERROR'
    ),

  /**
   * CANCEL CUSTOMER SUBSCRIPTION
   * Cancels a customer's active subscription
   */
  cancel: async (payload: {
    customerId: string;
    subscriptionId: string;
  }): Promise<ApiResponse<{ success: boolean }>> =>
    apiRequest<{ success: boolean }>(
      API_ENDPOINTS.subscriptions.customer.cancel,
      ApiMethods.POST,
      payload,
      'CUSTOMER_SUBSCRIPTION_CANCEL_ERROR'
    ),
};

/**
 * BUSINESS PROFILE API
 * SINGLETON RESOURCE (NOT CRUD) FOR BUSINESS PROFILE MANAGEMENT
 */
export const businessProfileApi = {
  /**
   * GET BUSINESS PROFILE
   */
  get: async (): Promise<ApiResponse<BusinessProfile | null>> =>
    apiRequest<BusinessProfile | null>(
      API_ENDPOINTS.businessProfile,
      ApiMethods.GET,
      null,
      'BUSINESS_PROFILE_FETCH_ERROR'
    ),

  /**
   * UPDATE BUSINESS PROFILE
   */
  update: async (payload: Partial<BusinessProfile>): Promise<ApiResponse<BusinessProfile>> =>
    apiRequest<BusinessProfile>(
      API_ENDPOINTS.businessProfile,
      ApiMethods.PUT,
      payload,
      'BUSINESS_PROFILE_UPDATE_ERROR'
    ),
};
